/**
 * The reveal — `FFL Pass 05 Draft Order.dc.html` 1d, `draft-order-brief.md` §3.
 *
 * Ten rows, ALL PRESENT FROM THE START, filling from the bottom up. Nothing is hidden, nothing
 * animates in from off-screen, and no row is added or removed while it runs: a GM watches the empty
 * rows above him shrink, which is the whole drama and also the whole information design. The rows
 * are `flex:1` inside a `flex:1` column so ten of them fit any phone height without a scroll.
 *
 * WHAT THIS FILE MAY NOT DO, and each is a line in §3 that was fought for twice in review:
 *
 *   IT MAY NOT DARKEN THE GREYS. Un-drawn position numbers are `#8a93a6` on `#131926` (5.70:1) and
 *   a drawn row's odds are `#98a2b3` on `#1e2635` (5.42:1). Those numerals ARE the content of this
 *   view — the position you are watching for and the weight that got somebody there. Both were
 *   raised twice; `tests/draftOrderChrome.test.ts` derives each ratio with the same `contrastRatio`
 *   the chips use and fails under 4.5:1. The em-dash placeholders at 1.82:1 are decorative and may
 *   stay decorative.
 *
 *   IT MAY NOT DO ARITHMETIC. Which positions are filled, and the share of the undrawn pool the
 *   viewer still holds, both come from `domain/lotteryReveal.ts`. The odds come from
 *   `LOTTERY_ODDS` by way of `weightOf`, never from a literal (§0.1).
 *
 * The clock is one stored instant, `draftState.lotteryDrawnAt`. Every GM's screen derives the same
 * row from the same origin, so "at the same moment" costs one write rather than ten broadcasts, and
 * somebody who opens the app mid-draw joins it in progress instead of watching it from the top.
 */

import { Fragment, useMemo } from 'react';
import { badgeMark, managerShort } from '../domain/identity.js';
import {
  DRAWN_POSITIONS, drawnCount, isDrawn, medalFor, ordinal, revealDurationMs, standingAfter,
  standingNote, weightOf,
} from '../domain/lotteryReveal.js';
import { Mark } from './avatars.js';
import { useNow } from './useNow.js';
import { RevealOddsPanel } from './RevealOdds.js';
import { enumerateOutcomes, type Revealed, type Weighted } from '../domain/revealOdds.js';
import { lotteryBlockDepth, placedByRule } from '../domain/draftOrder.js';

interface Props {
  /** Who chooses, in order. Index 0 is the first choice. */
  selectionOrder: readonly string[];
  /** Champion first, last place tenth — what the weights are read off. */
  finishOrder: readonly string[];
  /** The viewer, for the standing note and his own row. */
  me: string;
  /** When the draw was recorded. A rehearsal passes its own local start. */
  startedAt: string | number;
  teams: number;
  /** A rehearsal writes nothing and broadcasts nothing, and says so while it runs (§5). */
  rehearsal?: boolean;
  /**
   * The rows that were PLACED rather than drawn, marked on the board (Pass 06 §5b). All three
   * persist after the board holds — the declaration comes days later, and 4th needs to see which
   * row is the one he can lose.
   */
  toiletBowlWinner?: string | null;
  thirdPlace?: string | null;
  fourthPlace?: string | null;
  /**
   * The four the lottery produced. Without it the boundary is a guess and the "placed by rule"
   * tag is handed out on identity — see `placedByRule` and `lotteryBlockDepth`.
   */
  top4?: readonly string[];
  /** How many GMs are in the room, when the caller knows. Omitted rather than guessed. */
  present?: number | null;
  /** What the draw is opening, when there is one — "opening the Competition Committee". */
  occasion?: string | null;
  /** Offered once the tenth name has landed. The board holds until it is pressed. */
  onDismiss?: () => void;
}

export function LotteryReveal(
  {
    selectionOrder, finishOrder, me, startedAt, teams, rehearsal = false, onDismiss,
    toiletBowlWinner = null, thirdPlace = null, fourthPlace = null, top4,
    present = null, occasion = null,
  }: Props,
) {
  const origin = useMemo(() => new Date(startedAt).getTime(), [startedAt]);
  const total = revealDurationMs(teams);

  /*
   * Ticking only while there is something left to land. `useNow(null)` stops the interval dead, so
   * a finished board that a GM leaves open all evening is not re-rendering four times a second for
   * the rest of the night.
   */
  const settled = Date.now() - origin >= total;
  const now = useNow(settled ? null : 250);
  const elapsed = now - origin;
  const drawn = drawnCount(elapsed, teams);
  const done = drawn >= teams;

  const standing = useMemo(
    () => standingAfter({ selectionOrder, finishOrder, drawn, teams, me }),
    [selectionOrder, finishOrder, drawn, teams, me],
  );
  const note = standingNote(standing, teams);

  /*
   * THE PRIOR, and it is the one thing the odds panel gets that is not the revealed rows.
   * `finishOrder` here is the lottery BASIS (Pass 06 §1) — playoff places on top, regular season
   * underneath — so the weights are the ones the draw actually ran on.
   */
  const all = useMemo<Weighted[]>(
    () => finishOrder.map((gm) => ({ gm, weight: weightOf(finishOrder, gm) ?? 0 })),
    [finishOrder],
  );
  /* 5,040 ordered draws. Depends only on the prior, so it is walked once and handed back in. */
  const outcomes = useMemo(() => enumerateOutcomes(all), [all]);
  /*
   * What the room has seen, in the shape `revealOdds` wants: index 0 is board position 1, null
   * where the row is still dark. Derived from the same `isDrawn` the board draws itself with, so
   * the panel and the rows cannot disagree about what has landed.
   */
  const revealed = useMemo<Revealed>(
    () => Array.from({ length: teams }, (_, i) =>
      (isDrawn(i + 1, drawn, teams) ? selectionOrder[i] ?? null : null)),
    [selectionOrder, drawn, teams],
  );

  const up = (g: string | null | undefined) => (g ?? '').toUpperCase();
  const ruled = up(toiletBowlWinner);
  /*
   * WHERE THE LOTTERY STOPS, counted rather than typed.
   *
   * It was `DRAWN_POSITIONS + 1` — always five — and the Toilet Bowl placement is ADDITIVE, so in
   * an ordinary year the block is five deep and the line sat one row too high. On 26 August that
   * put Tim, who was drawn fourth, underneath a heading reading "the rest follow last season's
   * finish". In a year the Toilet Bowl winner wins the draw outright the block really is four, and
   * a typed five would have been wrong the other way.
   */
  const drawnFour = (top4 ?? []).map(up);
  const blockDepth = drawnFour.length
    ? lotteryBlockDepth(selectionOrder.map(up), drawnFour, ruled || null)
    : DRAWN_POSITIONS + (ruled && !selectionOrder.slice(0, DRAWN_POSITIONS).map(up).includes(ruled)
      ? 1 : 0);
  const third = up(thirdPlace);
  const fourth = up(fourthPlace);

  /** "26 August, 8:04pm ET" — the draw's own instant, in the league's clock. */
  const whenLabel = useMemo(() => whenIn(origin), [origin]);

  /*
   * THE LAST NAME — handoff §3, and it is the one panel on this screen that is not a result.
   *
   * At nine of ten there is one GM left and one position left, so he is not drawn: he is what is
   * remains. Saying so is more honest than letting the tenth card land as though the machine had
   * chosen it, and the room has already worked it out — three of the four placed forces the
   * fourth, which is exactly why the clock in front of position 1 was cut to 3.6s.
   */
  const forced = drawn === teams - 1 ? selectionOrder[0] ?? null : null;

  return (
    <section className="dolreveal" aria-live="polite">
      {rehearsal && (
        <p className="dolrehearse">
          <span className="dolrehearsedot" aria-hidden="true" />
          Rehearsal — nothing is saved
        </p>
      )}

      {/*
        THE STATUS BAR — handoff §3. It answers "what am I looking at" for the nine people in the
        room and the one on a phone in a car park: that this is happening now, when, and how much
        of it is left.

        WHAT IT DOES NOT SAY is anything it cannot know. The mock reads "nine present · opening the
        Competition Committee"; the room count is the launcher's and is not stored with the draw,
        so it appears when the caller passes it and the clause is absent otherwise. A status bar
        that invents its own facts is worse than a shorter one.
      */}
      <div className="dolrevhd">
        <span className={`dollive${rehearsal ? ' rehearsing' : ''}`}>
          <span className="dolldot" aria-hidden="true" />
          {rehearsal ? 'Rehearsing' : 'Drawing'}
        </span>
        <h2 className="dolvh">Choosing order</h2>
        <span className="dolrevwhen">
          {whenLabel}
          {present !== undefined && present !== null && <> &middot; {present} present</>}
          {occasion && <> &middot; {occasion}</>}
        </span>
        <span className="dolcount">
          {rehearsal ? `Rehearsing ${drawn} of ${teams} / nothing saved` : `${drawn} of ${teams} drawn`}
        </span>
      </div>

      <ol className="dolrows">
        {Array.from({ length: teams }, (_, i) => i + 1).map((position) => {
          const filled = isDrawn(position, drawn, teams);
          const gm = filled ? selectionOrder[position - 1] : undefined;
          /*
           * THE ROWS THAT WERE NOT WON (Pass 06 §5b).
           *
           * The Toilet Bowl winner is PLACED BY RULE (2020-II-1 §3), not drawn — his second
           * choice is guaranteed the moment he wins the consolation bracket. Every other filled
           * row carries the lottery weight that produced it, and on his that weight is a number
           * describing a draw his position did not come out of. Suppressed, and replaced with
           * what actually put him there.
           */
          /*
           * PLACED, NOT DRAWN — and only when that is true. The tag used to be `gm === ruled`, so
           * a Toilet Bowl winner who won the lottery outright was still labelled "placed by rule"
           * and had his weight suppressed, which is precisely the case 2020-II-1 §3 carves out.
           * With no `top4` to check — an old recorded draw — it falls back to the old reading
           * rather than claiming a provenance it cannot know.
           */
          const byRule = gm !== undefined && up(gm) === ruled
            && (drawnFour.length ? placedByRule(drawnFour, ruled) : true);
          const claim = gm === undefined ? null
            : up(gm) === third ? 'third'
              : up(gm) === fourth ? 'fourth' : null;
          const weight = gm && !byRule ? weightOf(finishOrder, gm) : null;
          const mine = gm !== undefined && gm.toUpperCase() === me.toUpperCase();
          const medal = medalFor(position);
          /*
           * ONE ROW ANIMATES, and it is the one that just landed. The board fills from the bottom,
           * so `teams - drawn + 1` is the newest position; giving only that row the class means a
           * GM who opens the app late gets a finished board rather than ten sparkles at once.
           */
          const just = filled && position === teams - drawn + 1;
          return (
            <Fragment key={position}>
              {/*
                2014-II-1 §2 drawn as a line. Everything above it was won in the draw; everything
                below it is last season's finish, worst first. The board used to render both halves
                identically, which is why nobody could tell that only four positions are a lottery.
              */}
              {position === blockDepth + 1 && (
                <li className="dolsplit" aria-hidden="true">
                  <span>the rest follow last season&rsquo;s finish</span>
                </li>
              )}
              <li
                className={[
                  'dolrow',
                  filled ? 'drawn' : '',
                  mine ? 'mine' : '',
                  medal && filled ? medal : '',
                  just ? 'just' : '',
                  byRule ? 'byrule' : '',
                  claim ? `claim ${claim}` : '',
                ].filter(Boolean).join(' ')}
              >
                <span className="dolpos">{position}</span>
                {gm ? (
                  <>
                    <Mark gm={gm} className="dolmark">{badgeMark(gm)}</Mark>
                    <span className="dolname">{managerShort(gm)}</span>
                    {weight !== null && <span className="dolwt"><span className="dolwtlab">weight </span>{weight}%</span>}
                    {byRule && (
                      <span className="dolbyrule" title="2020-II-1 §3 — the Toilet Bowl winner is guaranteed the second choice unless he wins the lottery outright.">
                        placed by rule
                      </span>
                    )}
                    {/*
                      BOTH MARKS NAME THE OTHER MAN (Tim, 25 August). They read "may swap" and
                      "third may take this", which states that something can happen to a row
                      without saying who it happens with — and on a ten-row board where two rows
                      are marked, the reader has to work out which two are paired. Naming the
                      counterpart makes each mark answer its own question.

                      "may take Matt's", not "can swap with Matt": the right is one-directional and
                      Matt has no say in it, so a word implying mutual agreement would be wrong
                      about the rule on the one screen where it is being exercised.

                      Both persist after the board holds — the declaration is days away, and until
                      it lands 4th's row is the one that can be taken off him.
                    */}
                    {claim === 'third' && fourthPlace && (
                      <span className="dolclaimmk" title={`${managerShort(thirdPlace ?? '')} may take ${managerShort(fourthPlace)}'s lottery result instead of his own (2018-II-5).`}>
                        may take {managerShort(fourthPlace)}&rsquo;s
                      </span>
                    )}
                    {claim === 'fourth' && thirdPlace && (
                      <span className="dolclaimmk mut" title={`${managerShort(thirdPlace)} may take this lottery result instead of his own (2018-II-5).`}>
                        {managerShort(thirdPlace)} may take this
                      </span>
                    )}
                  </>
                ) : (
                  // Decorative, and the one thing on this screen allowed to sit under AA (§3).
                  <span className="doldash" aria-hidden="true">&middot; &middot; &middot;</span>
                )}
                {/* The wipe. Purely decorative, and it plays once as the row fills. */}
                {just && <span className="dolwipe" aria-hidden="true" />}
              </li>
            </Fragment>
          );
        })}
      </ol>

      {/*
        The odds, under the board rather than beside it: at 390 there is no beside, and a panel
        that moves between the two breakpoints is two layouts to keep honest. It disappears when
        the last row lands, because there is nothing left to be uncertain about.
      */}
      {/*
        THE TOILET BOWL WINNER GOES IN, and the panel is wrong without him. He reaches the second
        choosing position by 2020-II-1 §3 rather than by the draw, so a model that reads his row as
        a drawn result matches no possible draw at all — see `boardFor`. Omitted here, the panel
        showed 0.0% against every remaining name on two boards in five.
      */}
      {forced && (
        <p className="dollast">
          <span className="dollastcap">The last name</span>
          <b>{managerShort(forced)}</b>
          <i>forced</i>
        </p>
      )}

      <RevealOddsPanel all={all} revealed={revealed} outcomes={outcomes} me={me}
        toiletBowl={toiletBowlWinner ?? null} />

      {/*
        The line GMs actually watch. It is a button only once the board is full — until then there
        is nothing to leave, and a bar that looks pressable while the draw is running invites
        somebody to press it and miss his own name.
      */}
      {/*
        WHERE YOU LANDED, as a figure rather than a sentence.
        Until his name lands this is the line a GM watches — how many are left and what share of
        the remaining weight he holds — so it stays prose. The moment he is placed it stops being a
        running commentary and becomes one fact, and one fact wants to be big.
      */}
      {standing.position !== null ? (
        <Placed
          position={standing.position}
          teams={teams}
          note={note}
          {...(done && onDismiss ? { onDismiss } : {})}
        />
      ) : (
        <p className="dolstand">{note}</p>
      )}
    </section>
  );
}


/**
 * The draw's instant, in the league's clock — "26 August, 8:04pm ET".
 *
 * ET on the face and nothing else: the room is in one time zone on the night, and a second clock
 * in a status bar that has to stay one line costs the sentence beside it.
 */
function whenIn(at: number): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', { month: 'long', day: 'numeric', timeZone: 'America/New_York' });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  }).replace(' AM', 'am').replace(' PM', 'pm');
  return `${day}, ${time} ET`;
}

/* -- where you landed -------------------------------------------------------------------------- */

function Placed(
  { position, teams, note, onDismiss }:
  { position: number; teams: number; note: string; onDismiss?: () => void },
) {
  const medal = medalFor(position);
  const body = (
    <>
      <span className="dolplacedlab">You choose</span>
      <span className="dolplacedfig">
        {position}<span className="dolplacedord">{ordinal(position)}</span>
        <span className="dolplacedof">of {teams}</span>
      </span>
      {medal && (
        <span className="dolplacedmedal">
          {medal === 'gold' ? 'Won the lottery'
            : medal === 'silver' ? 'Second in the draw'
              : medal === 'bronze' ? 'Third in the draw' : 'Fourth in the draw'}
        </span>
      )}
    </>
  );
  /* A control only once the board is full — the same rule the running note followed. */
  return onDismiss ? (
    <button type="button" className={`dolplaced act ${medal ?? ''}`} aria-label={note}
      onClick={onDismiss}>
      {body}
      <span className="dolstandgo" aria-hidden="true">&rsaquo;</span>
    </button>
  ) : (
    <p className={`dolplaced ${medal ?? ''}`} aria-label={note}>{body}</p>
  );
}
